import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { AlertTriangle, CircleCheckBig, RefreshCw } from "lucide-react";
import GoogleUserConnection from "./GoogleUserConnection";
import { connectApp, openLink } from "../api/AppLinkApi";
import { connectRedirectUrlAppIntegration } from "../Navigator";

const GoogleSetting = ({ appInfo }) => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  // null = not yet checked, true = token valid, false = token expired/invalid
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  // connected = token stored in DB; tokenValid = token actually works with Google API
  const isReallyConnected = appInfo.connected && tokenValid === true;
  const isTokenExpired = appInfo.connected && tokenValid === false;

  const handleReconnect = () => {
    setReconnecting(true);
    const postUrl = connectRedirectUrlAppIntegration(projectId, appInfo.app);
    connectApp(projectId, appInfo.app, postUrl)
      .then(({ redirectUrl = "" }) => {
        if (redirectUrl) openLink(redirectUrl);
      })
      .finally(() => setReconnecting(false));
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="col-span-12 lg:col-span-3">
          <div className={"w-40"}>{t("Google Profile")}</div>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <GoogleUserConnection onValidityChange={setTokenValid} />
        </div>
        <div className="col-span-12 lg:col-span-2">
          {isReallyConnected && (
            <div className="flex items-center justify-end mr-4 connected-anchor-element">
              <CircleCheckBig className="w-5 h-5 text-green-400 mr-2" />
              <Tooltip anchorSelect=".connected-anchor-element" content={t("Connected")} />
            </div>
          )}
          {isTokenExpired && (
            <div className="flex items-center justify-end mr-4 token-expired-anchor-element">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
              <Tooltip
                anchorSelect=".token-expired-anchor-element"
                content={t("Token expired — please reconnect your Google account.")}
              />
            </div>
          )}
        </div>
      </div>

      {isTokenExpired && (
        <div className="flex items-start gap-3 rounded-md px-4 py-3 text-sm bg-yellow-50 text-yellow-800 border border-yellow-200 mb-4">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">{t("Google token expired")}</p>
            <p className="text-yellow-700 mt-0.5">
              {t(
                "Your Google account token has expired or been revoked. Reconnect to restore access."
              )}
            </p>
          </div>
          <button
            onClick={handleReconnect}
            disabled={reconnecting}
            className="flex items-center gap-1.5 shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold bg-yellow-400 text-yellow-900 hover:bg-yellow-500 disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${reconnecting ? "animate-spin" : ""}`} />
            {reconnecting ? t("Reconnecting...") : t("Reconnect")}
          </button>
        </div>
      )}
    </>
  );
};

export default GoogleSetting;
